import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Container, Typography, Modal, CircularProgress } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguageContext } from '../contexts/LanguageContext';
import { getApiUrl } from '../config';

// Removed unused ReviewLocationState interface to resolve lint warning

const WorshipReviewPage: React.FC = () => {
  const { currentLanguage } = useLanguageContext();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state || {}) as any;
  const { videoUrl, genderType, worshipData, prayerId } = state;

  // Effective URL with fallbacks for revoked/stale object URLs
  const [effectiveUrl, setEffectiveUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [retryAttempted, setRetryAttempted] = useState<boolean>(false);
  const [mimeType, setMimeType] = useState<string>('video/webm');
  const [isConverting, setIsConverting] = useState<boolean>(false);
  const [isVideoSaved, setIsVideoSaved] = useState<boolean>(false);
  const [convertErrorKey, setConvertErrorKey] = useState<string | null>(null);
  const [isIOS, setIsIOS] = useState<boolean>(false);
  const [isSafari, setIsSafari] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [isWebmDownloadConfirmOpen, setIsWebmDownloadConfirmOpen] = useState<boolean>(false);
  const autoConvertStartedRef = useRef<boolean>(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // UA 기반 간단 탐지 (렌더에서 사용)
    try {
      const ua = navigator.userAgent || '';
      const isiOS = /iPad|iPhone|iPod/i.test(ua) || (ua.includes('Mac') && typeof (window as any).ontouchend !== 'undefined');
      const safari = /^((?!chrome|android).)*safari/i.test(ua);
      setIsIOS(!!isiOS);
      setIsSafari(!!safari);
    } catch (_) { /* ignore */ }

    let url: string | null = typeof videoUrl === 'string' ? videoUrl : null;
    const w = window as any;
    if (!url && typeof w.__lastRecordingObjectUrl === 'string') url = w.__lastRecordingObjectUrl;
    if (!url && w.__lastRecordingBlob instanceof Blob) {
      try {
        url = URL.createObjectURL(w.__lastRecordingBlob);
        w.__lastRecordingObjectUrl = url;
      } catch (_) {
        url = null;
      }
    }
    setEffectiveUrl(url);
    setLoadError(false);
    try {
      if (typeof (w as any).__lastRecordingMime === 'string' && (w as any).__lastRecordingMime.length > 0) {
        setMimeType((w as any).__lastRecordingMime);
      } else if ((w as any).__lastRecordingBlob instanceof Blob && typeof (w as any).__lastRecordingBlob.type === 'string') {
        setMimeType((w as any).__lastRecordingBlob.type || 'video/webm');
      }
    } catch (_) { /* ignore */ }
  }, [videoUrl]);

  const titlePrefix = useMemo(() => {
    if (currentLanguage === 'en') return genderType === 'sister' ? 'Sister' : 'Brother';
    return genderType === 'sister' ? '자매님' : '형제님';
  }, [genderType, currentLanguage]);

  const sharePrompt = useMemo(() => {
    if (currentLanguage === 'en') return `${titlePrefix}, please share your heartfelt prayer.`;
    return `${titlePrefix}의 진심과 간구를 나눠보세요.`;
  }, [currentLanguage, titlePrefix]);

  const handleNativeShare = async () => {
    try {
      if ((navigator as any).share) {
        await (navigator as any).share({
          title: 'He Listens to Me',
          text: currentLanguage === 'en' ? 'Share your worship' : '예배를 나누세요',
        });
      }
    } catch (_) { /* ignore */ }
  };

  const handleDownload = () => {
    if (!effectiveUrl) return;
    const a = document.createElement('a');
    a.href = effectiveUrl;
    const ext = (mimeType && mimeType.includes('mp4')) ? 'mp4' : 'webm';
    a.download = `worship-review.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // 다운로드 트리거 후 공유 버튼 노출
    window.setTimeout(() => setIsVideoSaved(true), 200);
  };

  const handleManualConvertToMp4 = async () => {
    if (!effectiveUrl) return;
    try {
      setIsConverting(true);
      const blob = await fetch(effectiveUrl).then((r) => r.blob());
      const form = new FormData();
      form.append('file', blob, 'recording.webm');
      const apiBase = getApiUrl('/worship/convert-video');
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 120000);
      let res: Response;
      try {
        res = await fetch(apiBase, { method: 'POST', body: form, signal: controller.signal });
      } finally {
        window.clearTimeout(timeoutId);
      }
      if (!res.ok) {
        if (res.status === 413) {
          throw new Error('convert-failed-entity-too-large');
        }
        throw new Error('convert-failed');
      }
      const mp4 = await res.blob();
      const url = URL.createObjectURL(mp4);
      setMimeType('video/mp4');
      setEffectiveUrl(url);
      setConvertErrorKey(null);
    } catch (_) {
      try {
        const err: any = _ as any;
        const msg = err?.message || '';
        if (msg === 'convert-failed-entity-too-large') {
          setConvertErrorKey('entity-too-large');
        } else if (msg === 'Failed to fetch' || msg === 'NetworkError when attempting to fetch resource.') {
          setConvertErrorKey('network');
        } else if ((err as any)?.name === 'AbortError') {
          setConvertErrorKey('timeout');
        } else {
          setConvertErrorKey('generic');
        }
      } catch (_) {
        setConvertErrorKey('generic');
      }
    } finally {
      setIsConverting(false);
    }
  };

  // 녹화 결과가 WebM이면(MP4 녹화 미지원 브라우저) 자동으로 MP4 변환
  useEffect(() => {
    if (!effectiveUrl || autoConvertStartedRef.current) return;
    const isWebm = typeof mimeType === 'string' && mimeType.toLowerCase().includes('webm');
    if (!isWebm) return;
    autoConvertStartedRef.current = true;
    handleManualConvertToMp4();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveUrl, mimeType]);

  // Show modal specifically when the uploaded file is too large for conversion
  useEffect(() => {
    if (convertErrorKey === 'entity-too-large') {
      setIsErrorModalOpen(true);
    }
  }, [convertErrorKey]);

  const openLink = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Simple platform navigations (no prefilled links)

  const handleShareInstagram = () => openLink('https://www.instagram.com/');

  const handleShareYouTube = () => openLink('https://www.youtube.com/');

  const handleShareKakao = () => openLink('https://www.kakao.com/talk');

  const handleShareFacebook = () => openLink('https://www.facebook.com/');

  const handleShareX = () => openLink('https://twitter.com/');

  const handleShareEmail = () => openLink('mailto:');

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(180deg, rgba(0,0,0,0.5), rgba(0,0,0,0.7))',
    }}>
      {/* 변환 진행 모달 */}
      <Modal
        open={!!isConverting}
        aria-labelledby="conversion-progress-title"
        aria-describedby="conversion-progress-description"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box sx={{
          backgroundColor: 'rgba(0,0,0,0.85)',
          color: 'white',
          borderRadius: 2,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          minWidth: 300
        }}>
          <CircularProgress color="inherit" size={32} thickness={4} />
          <Typography id="conversion-progress-title" variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
            {currentLanguage === 'en' ? 'Converting to MP4…' : 'MP4 파일로 변환하고 있습니다'}
          </Typography>
          <Typography id="conversion-progress-description" variant="body2" sx={{ opacity: 0.9, textAlign: 'center' }}>
            {currentLanguage === 'en' ? 'Please wait a moment.' : '잠시만 기다려주세요.'}
          </Typography>
        </Box>
      </Modal>

      {/* 변환 실패(용량 초과) 안내 모달 */}
      <Modal
        open={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
        aria-labelledby="conversion-error-title"
        aria-describedby="conversion-error-description"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box sx={{
          backgroundColor: 'rgba(0,0,0,0.85)',
          color: 'white',
          borderRadius: 2,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          minWidth: 320
        }}>
          <Typography id="conversion-error-title" variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
            {currentLanguage === 'en' ? 'Failed to create MP4 due to large file.' : '파일이 너무 커서 MP4 파일 생성에 실패했습니다.'}
          </Typography>
          <Typography id="conversion-error-description" variant="body2" sx={{ opacity: 0.9, textAlign: 'center' }}>
            {currentLanguage === 'en'
              ? "Reduce your browser window size and try 'Recreate video'."
              : "브라우저 화면 크기를 줄여서 '동영상 다시 생성하기'를 시도해보세요."}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={() => setIsErrorModalOpen(false)}>
              {currentLanguage === 'en' ? 'Close' : '닫기'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setIsErrorModalOpen(false);
                navigate('/worship', { state: { recreateVideo: true, genderType, worshipData, prayerId } });
              }}
            >
              {currentLanguage === 'en' ? 'Recreate video' : '동영상 다시 생성하기'}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* WebM 다운로드 확인 모달 */}
      <Modal
        open={isWebmDownloadConfirmOpen}
        onClose={() => setIsWebmDownloadConfirmOpen(false)}
        aria-labelledby="webm-download-title"
        aria-describedby="webm-download-description"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box sx={{
          backgroundColor: 'rgba(0,0,0,0.85)',
          color: 'white',
          borderRadius: 2,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          minWidth: 320
        }}>
          <Typography id="webm-download-title" variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
            {currentLanguage === 'en' ? 'Download as WebM' : 'WebM 파일로 다운로드됩니다.'}
          </Typography>
          <Typography id="webm-download-description" variant="body2" sx={{ opacity: 0.9, textAlign: 'center' }}>
            {currentLanguage === 'en'
              ? 'On iPhone, WebM may not be visible.'
              : 'iPhone에서는 보이지 않을 수 있습니다'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={() => setIsWebmDownloadConfirmOpen(false)}>
              {currentLanguage === 'en' ? 'Cancel' : '취소'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => { setIsWebmDownloadConfirmOpen(false); handleDownload(); }}
            >
              {currentLanguage === 'en' ? 'Confirm' : '확인'}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Container maxWidth="md" sx={{ textAlign: 'center' }}>
        <Typography variant="h3" sx={{ color: 'white', mb: 2, fontWeight: 'bold' }}>
          He Listens to Me
        </Typography>
        <Typography variant="h6" sx={{ color: 'white', mb: 3, whiteSpace: 'pre-line' }}>
          {sharePrompt}
        </Typography>

        <Box sx={{
          backgroundColor: 'transparent',
          border: '2px solid white',
          borderRadius: 3,
          p: 2,
          mb: 3,
        }}>
          {effectiveUrl && !loadError ? (
            <video
              controls
              style={{ width: '100%', borderRadius: 8 }}
              preload="none"
              playsInline
              onError={() => {
                if (retryAttempted) { setLoadError(true); return; }
                const w = window as any;
                if (w.__lastRecordingBlob instanceof Blob) {
                  try {
                    const newUrl = URL.createObjectURL(w.__lastRecordingBlob);
                    w.__lastRecordingObjectUrl = newUrl;
                    setRetryAttempted(true);
                    setEffectiveUrl(newUrl);
                    return;
                  } catch (_) { /* ignore */ }
                }
                setLoadError(true);
              }}
            >
              {effectiveUrl ? (
                <source src={effectiveUrl} type={mimeType || 'video/webm'} />
              ) : null}
            </video>
          ) : (
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>
              {currentLanguage === 'en' 
                ? (loadError ? 'Failed to load video' : 'No video available')
                : (loadError ? '동영상을 불러오지 못했습니다' : '동영상이 없습니다')}
            </Typography>
          )}
        </Box>

        {/* 1열: 동영상 다운로드, 동영상 다시 생성하기 */}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mb: 1 }}>
          {/* 동영상 다운로드: WebM이면 확인 모달 후 진행 */}
          <Button
            variant="outlined"
            onClick={() => {
              const isWebm = typeof mimeType === 'string' && mimeType.toLowerCase().includes('webm');
              if (isWebm) {
                setIsWebmDownloadConfirmOpen(true);
                return;
              }
              handleDownload();
            }}
            disabled={!effectiveUrl || isConverting}
          >
            {currentLanguage === 'en' ? 'Download video' : '동영상 다운로드'}
          </Button>

          {/* 동영상 다시 생성하기 */}
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              navigate('/worship', { state: { recreateVideo: true, genderType, worshipData, prayerId } });
            }}
          >
            {currentLanguage === 'en' ? 'Recreate video' : '동영상 다시 생성하기'}
          </Button>

          {(isIOS || isSafari) && isVideoSaved && (
            <Typography variant="body2" sx={{ alignSelf: 'center', color: 'white' }}>
              {currentLanguage === 'en' ? 'The video has been downloaded automatically.' : '동영상이 자동으로 다운로드되었습니다.'}
            </Typography>
          )}
        </Box>

        {/* 변환 실패 안내 메시지 및 행동 유도 */}
        {convertErrorKey && (
          <Box sx={{ mt: 1, mb: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              {(() => {
                if (convertErrorKey === 'entity-too-large') {
                  return currentLanguage === 'en'
                    ? 'The video file is too large to convert. Please recreate a shorter or lower-bitrate video.'
                    : '동영상 파일이 너무 커서 변환할 수 없습니다. 더 짧거나 낮은 비트레이트로 동영상을 다시 생성해 주세요.';
                }
                if (convertErrorKey === 'offline' || convertErrorKey === 'network') {
                  return currentLanguage === 'en'
                    ? 'Network connection is unstable or offline. Please try again later or recreate the video.'
                    : '네트워크 연결이 불안정하거나 오프라인 상태입니다. 잠시 후 다시 시도하거나 동영상을 다시 생성해 주세요.';
                }
                if (convertErrorKey === 'timeout') {
                  return currentLanguage === 'en'
                    ? 'Video conversion timed out. Please retry or recreate the video.'
                    : "브라우저 화면이 너무 커서 MP4 파일 변환에 실패했습니다. 브라우저 화면 크기를 1024 x 720 픽셀 이하로 줄여서 '동영상 다시 생성하기'를 시도해주세요.";
                }
                return currentLanguage === 'en'
                  ? 'Video conversion failed. Please try recreating the video.'
                  : '동영상 변환에 실패했습니다. 동영상을 다시 생성해 주세요.';
              })()}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/worship', { state: { recreateVideo: true, genderType, worshipData, prayerId } })}
            >
              {currentLanguage === 'en' ? 'Recreate video' : '동영상 다시 생성하기'}
            </Button>
          </Box>
        )}

        {/* 2열: Share, Instagram, YouTube, Kakaotalk, Facebook, X, Email (저장 후 노출) */}
        {isVideoSaved && (
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mb: 3 }}>
            {[
              { label: currentLanguage === 'en' ? 'Share' : '공유', onClick: handleNativeShare },
              { label: 'Instagram', onClick: handleShareInstagram },
              { label: 'YouTube', onClick: handleShareYouTube },
              { label: 'Kakaotalk', onClick: handleShareKakao },
              { label: 'Facebook', onClick: handleShareFacebook },
              { label: 'X', onClick: handleShareX },
              { label: 'Email', onClick: handleShareEmail },
            ].map(({ label, onClick }, idx) => {
              const isShare = idx === 0;
              return (
                <Button
                  key={label}
                  variant="contained"
                  onClick={onClick}
                  sx={{
                    backgroundColor: isShare ? 'transparent' : '#6F4E37',
                    color: '#ffffff',
                    border: isShare ? '1px solid #ffffff' : 'none',
                    transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
                    '&:hover': isShare
                      ? {
                          backgroundColor: 'transparent',
                          border: '1px solid #ffffff',
                        }
                      : { backgroundColor: '#5A3E2B' },
                    '&.Mui-disabled': {
                      backgroundColor: '#6F4E37',
                      color: '#ffffff',
                      opacity: 1,
                      border: isShare ? '1px solid #ffffff' : undefined,
                    },
                  }}
                >
                  {label}
                </Button>
              );
            })}
          </Box>
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/worship', { state: { jumpToChurchInvitation: true } })}
        >
          {currentLanguage === 'en' ? 'Finish worship' : '예배를 마칩니다'}
        </Button>
      </Container>
    </Box>
  );
};

export default WorshipReviewPage;


